// /banking-metrics — banking source/sink economy dashboard (staff-only).
// Epic #550 Phase 1, issue #559.
//
// Shows deposit/withdraw/net-flow per bank system (bank1, bank2) and aggregate.
// Interest accrual (type=2) is excluded from this dashboard — see API note.
// D-Currency-1: cash formatted with ¥ (U+00A5).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { EconomyDashboardLayout } from '@/components/admin/economy-dashboard-layout';

// ── Types ─────────────────────────────────────────────────────────────────────

interface BankSystemRow {
  system: string;
  deposit_volume: number;
  withdraw_volume: number;
  net_flow: number;
  deposit_count: number;
  withdraw_count: number;
  total_count: number;
}

interface MetricsResponse {
  ok: boolean;
  range: { since: number | null; until: number | null };
  systems: BankSystemRow[];
  totals: BankSystemRow;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Format an integer money value with thousands separators and the correct
 *  currency symbol per D-Currency-1 (cash = ¥ U+00A5, credits = ₢ U+20A2). */
function formatMoney(value: number, currency: string): string {
  const symbol = currency === 'credits' ? '₢' : '¥';
  return `${symbol}${value.toLocaleString()}`;
}

function buildQueryString(since: string, until: string, userId: string): string {
  const params = new URLSearchParams();
  if (since.trim()) params.set('since', since.trim());
  if (until.trim()) params.set('until', until.trim());
  if (userId.trim()) params.set('userId', userId.trim());
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function BankingTable({ rows }: { rows: BankSystemRow[] }) {
  return (
    <div className="overflow-x-auto rounded-sm border border-border">
      <table className="w-full text-sm" data-testid="banking-systems-table">
        <thead className="bg-muted text-left">
          <tr>
            <th className="px-3 py-2 font-medium text-ink-mid">System</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Deposits (¥)</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Withdrawals (¥)</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Net flow (¥)</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Deposit count</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Withdraw count</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Total txns</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-divider">
          {rows.map((row, idx) => (
            <tr key={idx} className="hover:bg-panel-elev">
              <td className="px-3 py-2 font-mono text-xs">{row.system}</td>
              <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(row.deposit_volume, 'cash')}</td>
              <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(row.withdraw_volume, 'cash')}</td>
              <td className={`px-3 py-2 font-mono text-xs tabular-nums ${row.net_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatMoney(row.net_flow, 'cash')}
              </td>
              <td className="px-3 py-2 tabular-nums">{row.deposit_count.toLocaleString()}</td>
              <td className="px-3 py-2 tabular-nums">{row.withdraw_count.toLocaleString()}</td>
              <td className="px-3 py-2 tabular-nums">{row.total_count.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function BankingMetricsPage() {
  // Filter form state (draft; applied on "Apply" click)
  const [draftSince, setDraftSince] = useState('');
  const [draftUntil, setDraftUntil] = useState('');
  const [draftUserId, setDraftUserId] = useState('');

  // Applied filters (trigger query)
  const [appliedSince, setAppliedSince] = useState('');
  const [appliedUntil, setAppliedUntil] = useState('');
  const [appliedUserId, setAppliedUserId] = useState('');

  const { data, isLoading, isError } = useQuery<MetricsResponse>({
    queryKey: ['banking-metrics', appliedSince, appliedUntil, appliedUserId],
    queryFn: async () => {
      const qs = buildQueryString(appliedSince, appliedUntil, appliedUserId);
      const res = await apiFetch<MetricsResponse>(`/admin/banking-metrics${qs}`);
      if (!res.ok) throw new Error('Failed to load banking metrics.');
      return res.data;
    },
  });

  function handleApply() {
    setAppliedSince(draftSince);
    setAppliedUntil(draftUntil);
    setAppliedUserId(draftUserId);
  }

  return (
    <EconomyDashboardLayout
      title="Banking Metrics"
      description="Deposit and withdrawal flows across both bank systems. Defaults to all-time; narrow by Unix timestamp range."
      since={draftSince}
      until={draftUntil}
      onSinceChange={setDraftSince}
      onUntilChange={setDraftUntil}
      userId={draftUserId}
      onUserIdChange={setDraftUserId}
      onApply={handleApply}
      isLoading={isLoading}
      isError={isError}
      errorMessage="Failed to load banking metrics."
    >
      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
        Interest accrual excluded (pending dedicated interest reporting, SUB-9).
      </p>

      {data != null && (
        <>
          {data.systems.length > 0 ? (
            <section className="space-y-2">
              <h2 className="text-lg font-medium text-ink-high">Per system + totals</h2>
              <BankingTable rows={[...data.systems, data.totals]} />
            </section>
          ) : (
            <p className="text-sm text-ink-low">No banking data available for this range.</p>
          )}
        </>
      )}
    </EconomyDashboardLayout>
  );
}
